import React, { useState, useEffect, useCallback } from 'react';
import Card from '@/components/shared/Card';
import Button from '@/components/shared/Button';
import DataTable from '@/components/shared/DataTable';
import Modal from '@/components/shared/Modal';
import { useAuth } from '@/contexts/AuthContext';
import { apiFetch } from '@/lib/api';
import { FiPlus, FiUserPlus, FiShield, FiUser, FiSave, FiX, FiCopy, FiCheck, FiLink } from 'react-icons/fi';

const ROLE_LABELS = {
  admin: { label: 'Admin', color: 'bg-red-100 text-red-800', icon: FiShield },
  manager: { label: 'Manager', color: 'bg-purple-100 text-purple-800', icon: FiUserPlus },
  sales: { label: 'Vertrieb', color: 'bg-blue-100 text-blue-800', icon: FiUser },
};

// Generiert ein lesbares Start-Passwort
const genPassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  return Array.from({ length: 10 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
};

const TeamManagement = () => {
  const { isAdmin } = useAuth();
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewModal, setShowNewModal] = useState(false);
  const [newForm, setNewForm] = useState({ full_name: '', email: '', role: 'sales', phone: '', password: genPassword() });
  const [createdUser, setCreatedUser] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  // GHL SSO-Link (geteilt, {{user.email}})
  const [showSsoModal, setShowSsoModal] = useState(false);
  const [ssoLink, setSsoLink] = useState('');
  const [ssoLoading, setSsoLoading] = useState(false);
  const [ssoError, setSsoError] = useState('');
  const [ssoCopied, setSsoCopied] = useState(false);

  const loadTeam = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await apiFetch('/api/users');
      const data = await resp.json();
      if (Array.isArray(data)) setTeam(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadTeam(); }, [loadTeam]);

  const openNewModal = () => {
    setNewForm({ full_name: '', email: '', role: 'sales', phone: '', password: genPassword() });
    setCreatedUser(null);
    setError('');
    setShowNewModal(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const resp = await apiFetch('/api/users', {
        method: 'POST',
        body: JSON.stringify(newForm),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setError(data.error?.includes('duplicate') || data.error?.includes('unique')
          ? 'Diese E-Mail-Adresse existiert bereits.'
          : (data.error || 'Anlegen fehlgeschlagen.'));
      } else {
        // Erfolg: Zugangsdaten anzeigen
        setCreatedUser({ ...data, password: newForm.password });
        loadTeam();
      }
    } catch (err) {
      setError('Server nicht erreichbar: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const openSsoLink = async () => {
    setShowSsoModal(true);
    setSsoLink('');
    setSsoError('');
    setSsoCopied(false);
    setSsoLoading(true);
    try {
      const resp = await apiFetch('/api/sso/ghl-link');
      const data = await resp.json();
      if (!resp.ok) {
        setSsoError(data.error || 'Link konnte nicht geladen werden.');
      } else if (!data.configured) {
        setSsoError('SSO ist serverseitig noch nicht konfiguriert (GHL_SSO_SECRET fehlt in der .env).');
      } else {
        setSsoLink(data.link);
      }
    } catch (err) {
      setSsoError('Server nicht erreichbar: ' + err.message);
    } finally {
      setSsoLoading(false);
    }
  };

  const copySsoLink = () => {
    navigator.clipboard.writeText(ssoLink).then(() => {
      setSsoCopied(true);
      setTimeout(() => setSsoCopied(false), 2000);
    });
  };

  const copyCredentials = () => {
    const text = `AIQCON Energy Zugangsdaten\nLink: https://energie.aiqcon.com\nE-Mail: ${createdUser.email}\nPasswort: ${createdUser.password}`;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const columns = [
    {
      key: 'name', label: 'Name', accessor: 'full_name',
      render: (row) => {
        const initials = (row.full_name || '?').split(' ').map(n => n[0]).join('').slice(0, 2);
        return (
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 bg-primary-100 rounded-full flex items-center justify-center">
              <span className="text-primary-700 text-xs font-medium">{initials}</span>
            </div>
            <div>
              <p className="font-medium text-gray-900">{row.full_name}</p>
              <p className="text-xs text-gray-500">{row.email}</p>
            </div>
          </div>
        );
      },
    },
    {
      key: 'role', label: 'Rolle', accessor: 'role',
      render: (row) => {
        const r = ROLE_LABELS[row.role] || ROLE_LABELS.sales;
        return (
          <span className={`inline-flex items-center space-x-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${r.color}`}>
            <r.icon size={12} />
            <span>{r.label}</span>
          </span>
        );
      },
    },
    { key: 'phone', label: 'Telefon', accessor: 'phone', render: (row) => row.phone || '–' },
    {
      key: 'status', label: 'Status', accessor: 'is_active',
      render: (row) => (
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
          row.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
        }`}>
          {row.is_active ? 'Aktiv' : 'Inaktiv'}
        </span>
      ),
    },
  ];

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none';

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team</h1>
          <p className="text-gray-500 mt-1">{team.length} Teammitglieder</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button variant="secondary" icon={FiLink} onClick={openSsoLink}>
              GHL-SSO-Link
            </Button>
            <Button icon={FiPlus} onClick={openNewModal}>
              Neues Mitglied
            </Button>
          </div>
        )}
      </div>

      {/* Rollen-Uebersicht */}
      <div className="grid grid-cols-3 gap-4">
        {Object.entries(ROLE_LABELS).map(([key, role]) => {
          const count = team.filter(t => t.role === key).length;
          return (
            <Card key={key} className="!p-4">
              <div className="flex items-center space-x-2">
                <role.icon className="text-gray-400" size={16} />
                <span className="text-sm text-gray-500">{role.label}</span>
              </div>
              <p className="text-2xl font-bold mt-1">{count}</p>
            </Card>
          );
        })}
      </div>

      <Card padding={false}>
        <div className="p-4">
          {loading ? (
            <p className="text-sm text-gray-400 py-8 text-center">Lade Team...</p>
          ) : (
            <DataTable columns={columns} data={team} searchPlaceholder="Teammitglied suchen..." />
          )}
        </div>
      </Card>

      {!isAdmin && (
        <Card className="bg-amber-50 border-amber-200">
          <p className="text-sm text-amber-800">
            Nur Administratoren koennen Teammitglieder hinzufuegen oder Rollen aendern.
          </p>
        </Card>
      )}

      <Modal isOpen={showNewModal} onClose={() => setShowNewModal(false)} title={createdUser ? 'Mitglied angelegt' : 'Neues Teammitglied'}>
        {!createdUser ? (
          <form onSubmit={handleCreate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input value={newForm.full_name} onChange={e => setNewForm(f => ({ ...f, full_name: e.target.value }))} className={inputClass} placeholder="Vor- und Nachname" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail *</label>
              <input type="email" value={newForm.email} onChange={e => setNewForm(f => ({ ...f, email: e.target.value }))} className={inputClass} placeholder="name@aiqcon.com" required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rolle</label>
                <select value={newForm.role} onChange={e => setNewForm(f => ({ ...f, role: e.target.value }))} className={inputClass}>
                  <option value="sales">Vertrieb</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
                <input value={newForm.phone} onChange={e => setNewForm(f => ({ ...f, phone: e.target.value }))} className={inputClass} placeholder="+49 170 ..." />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start-Passwort *</label>
              <div className="flex gap-2">
                <input value={newForm.password} onChange={e => setNewForm(f => ({ ...f, password: e.target.value }))} className={inputClass} required />
                <Button type="button" variant="secondary" onClick={() => setNewForm(f => ({ ...f, password: genPassword() }))}>Neu</Button>
              </div>
              <p className="text-xs text-gray-400 mt-1">Der Mitarbeiter sollte es nach dem ersten Login aendern.</p>
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex justify-end space-x-3 pt-2 border-t">
              <Button type="button" variant="secondary" icon={FiX} onClick={() => setShowNewModal(false)}>Abbrechen</Button>
              <Button type="submit" icon={FiSave} disabled={saving}>{saving ? 'Speichern...' : 'Anlegen'}</Button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <FiCheck className="mx-auto text-green-600 mb-2" size={24} />
              <p className="text-sm text-green-700 font-medium">{createdUser.full_name} wurde angelegt!</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm font-mono">
              <div><span className="text-gray-500">Link:</span> https://energie.aiqcon.com</div>
              <div><span className="text-gray-500">E-Mail:</span> {createdUser.email}</div>
              <div><span className="text-gray-500">Passwort:</span> {createdUser.password}</div>
            </div>
            <button
              onClick={copyCredentials}
              className={`w-full py-2.5 rounded-lg font-medium text-sm flex items-center justify-center gap-2 ${copied ? 'bg-green-100 text-green-700' : 'bg-primary-600 hover:bg-primary-700 text-white'}`}
            >
              {copied ? <><FiCheck size={16} /> Kopiert!</> : <><FiCopy size={16} /> Zugangsdaten kopieren</>}
            </button>
            <p className="text-xs text-gray-400 text-center">
              Geben Sie diese Zugangsdaten dem neuen Mitarbeiter. Das Passwort ist danach nicht mehr einsehbar.
            </p>
            <div className="flex justify-end pt-2 border-t">
              <Button type="button" onClick={() => setShowNewModal(false)}>Fertig</Button>
            </div>
          </div>
        )}
      </Modal>

      {/* GHL-SSO-Link Modal (geteilt) */}
      <Modal isOpen={showSsoModal} onClose={() => setShowSsoModal(false)} title="GHL-SSO-Link (für alle Nutzer)">
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            <strong>Ein</strong> geteilter Link für das ganze Team. GHL setzt automatisch die E-Mail des
            eingeloggten Nutzers ein (<code>{'{{user.email}}'}</code>) – jeder landet in <strong>seinem eigenen</strong>
            Energie-Portal. In GoHighLevel als <strong>Custom Menu Link</strong> hinterlegen, „Open in: iFrame".
          </p>

          {ssoLoading ? (
            <p className="text-sm text-gray-400 py-6 text-center">Lade Link …</p>
          ) : ssoError ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">{ssoError}</div>
          ) : ssoLink ? (
            <>
              <div className="bg-gray-50 rounded-lg p-3 text-xs font-mono break-all border border-gray-200">
                {ssoLink}
              </div>
              <button
                onClick={copySsoLink}
                className={`w-full py-2.5 rounded-lg font-medium text-sm flex items-center justify-center gap-2 ${ssoCopied ? 'bg-green-100 text-green-700' : 'bg-primary-600 hover:bg-primary-700 text-white'}`}
              >
                {ssoCopied ? <><FiCheck size={16} /> Kopiert!</> : <><FiCopy size={16} /> SSO-Link kopieren</>}
              </button>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 space-y-1">
                <p className="font-semibold">Hinweis</p>
                <p>Der Nutzer muss als GHL-User existieren; ein passender Plattform-Account wird beim ersten Login
                automatisch angelegt. Der geheime Schlüssel im Link sichert gegen Zugriff von außen.</p>
              </div>

              <div className="flex justify-end pt-2 border-t">
                <Button type="button" onClick={() => setShowSsoModal(false)}>Fertig</Button>
              </div>
            </>
          ) : null}
        </div>
      </Modal>
    </div>
  );
};

export default TeamManagement;
